import { Ref, onMounted, onUnmounted, unref } from 'vue'

const MODIFIER_ORDER = ['meta', 'ctrl', 'alt', 'shift'] as const
const MODIFIER_KEYS = new Set(MODIFIER_ORDER)
const MODIFIER_ALIASES: Record<string, (typeof MODIFIER_ORDER)[number]> = {
  command: 'meta',
  cmd: 'meta',
  control: 'ctrl',
  option: 'alt',
  super: 'meta',
}

type KeydownEventTarget = Pick<Window, 'addEventListener' | 'removeEventListener'>
type MaybeRef<T> = Ref<T> | T

type UseKeydownEventListenerOptions = {
  target?: MaybeRef<KeydownEventTarget | null | undefined>
  disableWhileTypingInFormControls?: boolean
  capture?: boolean
  stopPropagation?: boolean
}

const normalizeShortcut = (shortcut: string) => {
  const parts = shortcut
    .split('+')
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean)

  const modifiers: string[] = []
  let key = ''

  for (const part of parts) {
    const normalizedPart = MODIFIER_ALIASES[part] ?? part
    if (MODIFIER_KEYS.has(normalizedPart as (typeof MODIFIER_ORDER)[number])) {
      if (!modifiers.includes(normalizedPart)) {
        modifiers.push(normalizedPart)
      }
    } else if (!key) {
      key = normalizedPart
    }
  }

  if (!key) {
    return ''
  }

  modifiers.sort(
    (a, b) =>
      MODIFIER_ORDER.indexOf(a as (typeof MODIFIER_ORDER)[number]) -
      MODIFIER_ORDER.indexOf(b as (typeof MODIFIER_ORDER)[number]),
  )

  return modifiers.length ? `${modifiers.join('+')}+${key}` : key
}

const normalizeEventKey = (event: KeyboardEvent) => {
  const key = event.key.toLowerCase()
  const code = event.code ?? ''

  if (code.startsWith('Digit')) {
    return code.slice('Digit'.length).toLowerCase()
  }

  if (code.startsWith('Numpad')) {
    const numpadKey = code.slice('Numpad'.length).toLowerCase()
    if (/^\d$/.test(numpadKey)) {
      return numpadKey
    }
  }

  return key
}

export function useKeydownEventListener(
  shortcuts: Record<string, () => void>,
  options: UseKeydownEventListenerOptions = {},
) {
  const shortcutMap = Object.entries(shortcuts).reduce<Record<string, () => void>>(
    (acc, [shortcut, handler]) => {
      const normalized = normalizeShortcut(shortcut)
      if (normalized) {
        acc[normalized] = handler
      }
      return acc
    },
    {},
  )

  const handleKeyDown = (event: KeyboardEvent) => {
    const disableWhileTypingInFormControls = options.disableWhileTypingInFormControls ?? true

    const isInputText = ['INPUT', 'TEXTAREA', 'SELECT'].includes(
      document.activeElement?.tagName ?? '',
    )
    if (disableWhileTypingInFormControls && isInputText) {
      return
    }

    if (!event.key) {
      return
    }

    const key = normalizeEventKey(event)
    if (MODIFIER_KEYS.has(key as (typeof MODIFIER_ORDER)[number])) {
      return
    }

    const modifiers: string[] = []
    if (event.metaKey) modifiers.push('meta')
    if (event.ctrlKey) modifiers.push('ctrl')
    if (event.altKey) modifiers.push('alt')
    if (event.shiftKey && key !== 'shift') modifiers.push('shift')

    const normalizedKey = modifiers.length
      ? `${modifiers
          .sort(
            (a, b) =>
              MODIFIER_ORDER.indexOf(a as (typeof MODIFIER_ORDER)[number]) -
              MODIFIER_ORDER.indexOf(b as (typeof MODIFIER_ORDER)[number]),
          )
          .join('+')}+${key}`
      : key

    const handler = shortcutMap[normalizedKey]
    if (handler) {
      event.preventDefault()
      if (options.stopPropagation) {
        event.stopPropagation()
      }
      handler()
    }
  }

  onMounted(() => {
    const target = unref(options.target) ?? window
    target.addEventListener('keydown', handleKeyDown, {
      capture: options.capture,
    })
  })
  onUnmounted(() => {
    const target = unref(options.target) ?? window
    target.removeEventListener('keydown', handleKeyDown, {
      capture: options.capture,
    })
  })
}
